import React, { useState, useEffect } from 'react';
import { EyeIcon, EyeSlashIcon, InboxStackIcon, RectangleStackIcon } from '@heroicons/react/24/outline';
import Card, { CardHeader } from '../ui/Card';
import { memberProfileAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { apiErrorMessage } from '../../utils/apiError';

/**
 * Booking access controls (relocated from the Profile page in the Profile/Settings
 * reorg). Operational config — "how clients find and book me" — so it lives in
 * Settings, not on the public-facing Profile. Saves immediately (no edit mode).
 *
 *  - Visibility: public (in directory) vs private (invite code only)
 *  - Booking approval: direct booking vs request-and-approve (public only)
 */
const Toggle: React.FC<{ on: boolean; onClick: () => void; tone?: 'primary' | 'gold' }> = ({ on, onClick, tone = 'primary' }) => (
  <button
    onClick={onClick}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-clinic-primary focus:ring-offset-2 ${
      on ? (tone === 'gold' ? 'bg-clinic-gold' : 'bg-clinic-primary') : 'bg-clinic-surface-sunken'
    }`}
  >
    <span
      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
        on ? 'translate-x-5' : 'translate-x-0'
      }`}
    />
  </button>
);

const BookingAccessCard: React.FC = () => {
  const { updateUser } = useAuth();
  const [isPublic, setIsPublic] = useState(true);
  const [requiresApproval, setRequiresApproval] = useState(false);
  const [serviceMode, setServiceMode] = useState(false);
  const [serviceCount, setServiceCount] = useState(0);
  // Distinguish "confirmed zero services" from "we couldn't load them" — a failed
  // fetch shouldn't show the "add services" hint or hard-block the toggle.
  const [servicesKnown, setServicesKnown] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const p = await memberProfileAPI.getProfile();
        setIsPublic(p.is_public ?? true);
        setRequiresApproval(p.requires_booking_approval ?? false);
        setServiceMode(p.booking_mode === 'service');
      } catch {
        /* non-fatal */
      }
      try {
        const services = await memberProfileAPI.listServices();
        setServiceCount(services.length);
        setServicesKnown(true);
      } catch {
        setServicesKnown(false);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggleVisibility = async () => {
    const next = !isPublic;
    const prevPublic = isPublic;
    const prevApproval = requiresApproval;
    // Going private also clears approval (backend enforces this rule).
    setIsPublic(next);
    if (!next) setRequiresApproval(false);
    try {
      const updated = await memberProfileAPI.updateProfile({ is_public: next });
      setIsPublic(updated.is_public ?? next);
      setRequiresApproval(updated.requires_booking_approval ?? false);
      updateUser({
        is_public: updated.is_public,
        requires_booking_approval: updated.requires_booking_approval ?? false,
      });
      toast.success(next ? 'Profile is now public' : 'Profile is now private');
    } catch (err: any) {
      setIsPublic(prevPublic);
      setRequiresApproval(prevApproval);
      toast.error(apiErrorMessage(err, 'Failed to update visibility'));
    }
  };

  const toggleApproval = async () => {
    const next = !requiresApproval;
    setRequiresApproval(next);
    try {
      const updated = await memberProfileAPI.updateProfile({ requires_booking_approval: next });
      setRequiresApproval(updated.requires_booking_approval ?? false);
      updateUser({ requires_booking_approval: updated.requires_booking_approval ?? false });
      toast.success(next ? 'Booking approval now required' : 'Direct booking enabled');
    } catch (err: any) {
      setRequiresApproval(!next);
      toast.error(apiErrorMessage(err, 'Failed to update booking approval'));
    }
  };

  const toggleBookingMethod = async () => {
    const next = !serviceMode;
    // Can't switch to service-based without at least one service. Only hard-block
    // when we KNOW the count is zero; if the services fetch failed, let the backend
    // guard decide (it returns a clear 400) rather than blocking on stale state.
    if (next && servicesKnown && serviceCount === 0) {
      toast.error('Add at least one service on your Profile first.');
      return;
    }
    setServiceMode(next);
    try {
      const updated = await memberProfileAPI.updateProfile({ booking_mode: next ? 'service' : 'time' });
      setServiceMode(updated.booking_mode === 'service');
      toast.success(next ? 'Patients now book by service' : 'Patients now choose a time + duration');
    } catch (err: any) {
      setServiceMode(!next);
      toast.error(apiErrorMessage(err, 'Failed to update booking method'));
    }
  };

  return (
    <Card className="p-6">
      <CardHeader title="Booking Access" />
      {loading ? (
        <p className="text-sm text-clinic-muted">Loading…</p>
      ) : (
        <div className="space-y-5">
          {/* Visibility */}
          <div>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                {isPublic ? (
                  <EyeIcon className="h-5 w-5 text-clinic-primary mr-2" />
                ) : (
                  <EyeSlashIcon className="h-5 w-5 text-clinic-muted mr-2" />
                )}
                <span className="text-sm font-medium text-clinic-dark">{isPublic ? 'Public' : 'Private'}</span>
              </div>
              <Toggle on={isPublic} onClick={toggleVisibility} />
            </div>
            <p className="text-xs text-clinic-muted mt-2">
              {isPublic
                ? 'Your profile is visible in the public clinician directory.'
                : 'Your profile is hidden from the directory. Only clients with your invite code can find you.'}
            </p>
          </div>

          {/* Approval — only meaningful when public */}
          {isPublic && (
            <div className="pt-5 border-t border-clinic-primary-lighter">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <InboxStackIcon className={`h-5 w-5 mr-2 ${requiresApproval ? 'text-clinic-gold' : 'text-clinic-muted'}`} />
                  <span className="text-sm font-medium text-clinic-dark">
                    {requiresApproval ? 'Approval Required' : 'Direct Booking'}
                  </span>
                </div>
                <Toggle on={requiresApproval} onClick={toggleApproval} tone="gold" />
              </div>
              <p className="text-xs text-clinic-muted mt-2">
                {requiresApproval
                  ? 'Clients request appointments and you approve each one.'
                  : 'Clients can book available slots directly.'}
              </p>
            </div>
          )}

          {/* Booking method — pick a service vs choose a duration */}
          <div className="pt-5 border-t border-clinic-primary-lighter">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <RectangleStackIcon className={`h-5 w-5 mr-2 ${serviceMode ? 'text-clinic-primary' : 'text-clinic-muted'}`} />
                <span className="text-sm font-medium text-clinic-dark">
                  {serviceMode ? 'Book by Service' : 'Book by Time'}
                </span>
              </div>
              <Toggle on={serviceMode} onClick={toggleBookingMethod} />
            </div>
            <p className="text-xs text-clinic-muted mt-2">
              {serviceMode
                ? 'Clients pick one of your services; the duration is set by that service.'
                : 'Clients choose a start time and duration themselves.'}
              {servicesKnown && serviceCount === 0 && (
                <span className="block mt-1 text-clinic-gold">Add services on your Profile to enable booking by service.</span>
              )}
            </p>
          </div>
        </div>
      )}
    </Card>
  );
};

export default BookingAccessCard;
